import {
  Grid,
  MenuItem,
  Select,
  Button,
  TextField,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { IFilterParams } from "ag-grid-community";
import { IFilterReactComp } from "ag-grid-react";
import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";

type DeviceFilterProps = IFilterParams & { showPercent?: boolean };

const DeviceFilter = forwardRef<IFilterReactComp, DeviceFilterProps>(
  ({ filterChangedCallback, valueGetter, showPercent = false }, ref) => {
    const [numberOrPercent, setNumberOrPercent] = useState("number");
    const [filterActive, setFilterActive] = useState(false);
    const [trigger, setTrigger] = useState(false);
    const [type, setType] = useState<"greaterThanOrEqual" | "lessThanOrEqual" | "between">(
      "between"
    );
    const [from, setFrom] = useState<"" | number>("");
    const [to, setTo] = useState<"" | number>("");

    // expose AG Grid Filter Lifecycle callbacks
    useImperativeHandle(ref, () => {
      return {
        doesFilterPass(params) {
          const value = valueGetter(params.node);
          if (numberOrPercent === "number") {
            if (type === "greaterThanOrEqual") {
              return value !== "" && value >= from!;
            } else if (type === "lessThanOrEqual") {
              return value !== "" && value <= to!;
            } else {
              return value !== "" && value >= from! && value <= to!;
            }
          } else {
            return value;
          }
        },

        isFilterActive() {
          return filterActive;
        },

        getModel() {
          if (!filterActive) return null;
          const savedStatus = {
            numberOrPercent,
            type,
            from: numberOrPercent === "percent" ? (from || 0) / 100 : from,
            to: numberOrPercent === "percent" ? (to || 0) / 100 : to,
          };
          if (numberOrPercent === "number") {
            if (type === "between") {
              return {
                type: "between",
                filter: `${from}|${to}`,
                savedStatus,
              };
            } else {
              return {
                type,
                filter: from,
                savedStatus,
              };
            }
          } else {
            if (type === "between") {
              return {
                type: "between",
                filter: `${(from || 0) / 100}|${(to || 0) / 100}`,
                savedStatus,
              };
            } else {
              return {
                type,
                filter: (from || 0) / 100,
                savedStatus,
              };
            }
          }
        },

        setModel(model: any) {
          if (model?.savedStatus) {
            const { savedStatus } = model;
            setNumberOrPercent(savedStatus.numberOrPercent ?? "number");
            setType(savedStatus.type ?? "between");
            if (savedStatus.numberOrPercent === "percent") {
              setFrom(savedStatus.from ? savedStatus.from * 100 : "");
              setTo(savedStatus.to ? savedStatus.to * 100 : "");
            } else {
              setFrom(savedStatus.from ?? "");
              setTo(savedStatus.to ?? "");
            }
            setFilterActive(true);
            setTrigger(true);
          } else {
            setFilterActive(false);
            setFrom("");
            setTo("");
            setTrigger(true);
          }
        },
      };
    });

    const onClear = () => {
      setFilterActive(false);
      setFrom("");
      setTo("");
      setTrigger(true);
    };

    const onApply = () => {
      setFilterActive(true);
      setTrigger(true);
    };

    useEffect(() => {
      if (trigger) {
        filterChangedCallback();
        setTrigger(false);
      }
    }, [trigger, filterChangedCallback]);

    return (
      <Grid
        container={true}
        direction="column"
        className="ag-filter-body-wrapper ag-simple-filter-body-wrapper"
      >
        {showPercent ? (
          <RadioGroup
            value={numberOrPercent}
            //@ts-ignore
            onChange={(_e, v) => {
              setNumberOrPercent(v);
              setFrom("");
              setTo("");
            }}
            style={{ flexDirection: "row" }}
          >
            <FormControlLabel
              value="number"
              control={<Radio color="primary" />}
              label="Numeric"
            />
            <FormControlLabel
              value="percent"
              control={<Radio color="primary" />}
              label="Percent"
            />
          </RadioGroup>
        ) : null}
        <Grid
          container={true}
          direction="column"
          spacing={1}
        >
          <Grid item={true}>
            <Select
              fullWidth={true}
              variant="outlined"
              margin="dense"
              value={type}
              size="small"
              //@ts-ignore
              onChange={(e) => setType(e.target.value)}
              MenuProps={{
                disablePortal: true,
                keepMounted: true,
              }}
            >
              <MenuItem value="between">Between</MenuItem>
              <MenuItem value="greaterThanOrEqual">Greater Than or Equal</MenuItem>
              <MenuItem value="lessThanOrEqual">Less Than or Equal</MenuItem>
            </Select>
          </Grid>
          <Grid item={true}>
            <TextField
              fullWidth={true}
              type="number"
              placeholder="Please input number"
              variant="standard"
              value={from}
              onChange={(e) => {
                setFrom(
                  e.target.value === ""
                    ? ""
                    : numberOrPercent === "percent"
                      ? +e.target.value > 100
                        ? 100
                        : +e.target.value < 0
                          ? 0
                          : +e.target.value
                      : +e.target.value
                );
              }}
              InputProps={{
                endAdornment:
                  numberOrPercent === "percent" ? (
                    <InputAdornment position="end">
                      <b>%</b>
                    </InputAdornment>
                  ) : undefined,
              }}
            />
          </Grid>
          {type === "between" ? (
            <Grid item={true}>
              <TextField
                fullWidth={true}
                type="number"
                placeholder="To..."
                variant="standard"
                value={to}
                onChange={(e) => {
                  setTo(
                    e.target.value === ""
                      ? ""
                      : numberOrPercent === "percent"
                        ? +e.target.value > 100
                          ? 100
                          : +e.target.value < 0
                            ? 0
                            : +e.target.value
                        : +e.target.value
                  );
                }}
                InputProps={{
                  endAdornment:
                    numberOrPercent === "percent" ? (
                      <InputAdornment position="end">
                        <b>%</b>
                      </InputAdornment>
                    ) : undefined,
                }}
              />
            </Grid>
          ) : null}
        </Grid>
        <Grid
          item={true}
          container={true}
          justifyContent="space-evenly"
        >
          <Grid item={true}>
            <Button
              variant="text"
              disabled={!filterActive}
              onClick={onClear}
              className="ag-standard-button ag-filter-apply-panel-button"
            >
              Reset filter
            </Button>
          </Grid>
          <Grid item={true}>
            <Button
              variant="text"
              className="ag-standard-button ag-filter-apply-panel-button"
              onClick={onApply}
              disabled={from === "" || (type === "between" && (to === "" || +to < +from))}
            >
              Apply filter
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export default DeviceFilter;
